import { HighlightLegend } from '@/components/ECharts';
import { ToggleSelect } from '@/components/ToggleSelect';
import { WithLoader } from '@/components/WithLoader';
import { CompetitorMediaEffectGraph } from './components/CompetitorMediaEffectGraph';
import { useCompetitorsMediaInvestmentContext } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentContext';
import { Typography } from '@analytical-alley/ui';
import EChartsReact from 'echarts-for-react';
import React, { useMemo, useRef } from 'react';

type SeriesData = {
    name: string;
    color: string;
};

type ChartOptions = Array<SeriesData>;

export const CompetitorMediaEffectSection = () => {
    const {
        competitorsMediaEffectData: {
            variableData,
            totalInvestment: totalEffect,
        },
        isLoading,
        isFetching,
        selectedCompetitorFilter,
        setSelectedCompetitor,
    } = useCompetitorsMediaInvestmentContext();

    const chartRef = useRef<EChartsReact>(null);
    const [selected, setSelected] = React.useState<string>('');
    const [seriesData, setSeriesData] = React.useState<SeriesData[]>([]);

    const onSelected = (value: string) => {
        setSelected((prev: string) => {
            if (prev || prev === value) {
                return '';
            }
            return value;
        });
    };

    const getChartOptions = (chartOptions: ChartOptions) => {
        setSeriesData(chartOptions);
    };

    const competitorFiltersWithImpact = useMemo(() => {
        const impactSet = new Set(variableData.map((obj) => obj.variable));

        return selectedCompetitorFilter.filter((obj) =>
            impactSet.has(obj.name),
        );
    }, [selectedCompetitorFilter, variableData]);

    const isEmptyResults = !isFetching && !isLoading && totalEffect === 0;

    if (isEmptyResults) {
        return (
            <div className="glass-tile p-6">
                <div className="flex justify-between mb-4">
                    <Typography variant="titleS">
                        Competitor Media Effect
                    </Typography>
                </div>
                <div className="relative w-full">
                    <Typography
                        className="font-mono text-center"
                        variant="bodyM"
                    >
                        No impact on your KPI was detected!
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="glass-tile p-6">
            <WithLoader isLoading={isLoading} isFetching={isFetching}>
                <div className="flex justify-between mb-4">
                    <Typography variant="titleS">
                        Competitor Media Effect
                    </Typography>
                    <ToggleSelect
                        items={competitorFiltersWithImpact?.map((item) => ({
                            label: item.name,
                            value: item.name,
                            checked: item.selected,
                        }))}
                        placement={'bottom-end'}
                        compact
                        label={'Competitors'}
                        showAllCheckbox={true}
                        onChange={(name, checked) => {
                            setSelectedCompetitor(name, checked);
                        }}
                    />
                </div>
                <CompetitorMediaEffectGraph
                    chartRef={chartRef}
                    selected={selected}
                    onSelected={onSelected}
                    getChartOptions={getChartOptions}
                />
                <div className="relative mt-5 pl-14 pr-4 z-20">
                    <HighlightLegend
                        highlighted={selected}
                        legendItems={seriesData?.map((s) => ({
                            name: String(s.name),
                            highlighted: true,
                            color: s.color,
                        }))}
                        onClick={(name) => {
                            if (selected === name) {
                                setSelected('');
                                return;
                            }
                            setSelected(name);
                        }}
                    />
                </div>
            </WithLoader>
        </div>
    );
};
